import { Prompt, useHistory } from 'react-router-dom';

import { useState, FunctionComponent, useEffect, useMemo } from 'react';

import { PaymentFields, PaymentFieldDefault } from '@/hooks/useFields/TransactionCreationFields/types';
import styles from './SinglePaymentForm.module.scss';
import sharedStyles from '@/styles/PaymentAndCollection/PaymentAndCollection.module.scss';
import {
  Box,
  Modal,
  Card,
  CollapsibleCard,
  toBool,
  withStyles,
  useForm,
  SupportedCurrencies,
  PaymentMethod,
} from 'vopay';
import { Button, Title } from '@/components/UI';
const classes = withStyles({ ...styles, ...sharedStyles });
import {
  useContactAddressFields,
  useElinxBankAccountFields,
  useContactDetailsFields,
  useElinxDetailsFields,
  useInteracEtransferFields,
  useBankAccountFields,
  usePaymentSettingsFields,
  useTransactionDetailsFields,
  useAdditionalInformation,
  useElinxSettingsFields,
  useContactFields,
  useAmountFields,
  useElinxCardFields,
} from '@/hooks/useFields/TransactionCreationFields';
import useInteracAutoDeposit from '@/hooks/useApi/useInteracAutoDeposit';
import useFetchClientAccountBalance from '@/hooks/useApi/useFetchClientAccountBalance';
import { useLockedFeatureDialogue } from '@/providers/LockedFeatureDialogue.provider';
import { useLockedFeatures } from '@/providers/LockedFeatures.provider';
import { usePermissions } from '@/providers/Permissions.provider';
import ElinxImage from '../Images/elinx-preview.png';
import { useClientData } from '@/providers/ClientData.provider';
import ElinxSettingsDialogue from '@/components/Elinx/ElinxSettings/ElinxSettingsDialogue';

import { useAlertDialog } from '@/providers/AlertDialog.provider';
import useFetchElinxSettings from '@/hooks/useApi/useFetchElinxSettings';
import useGLCodes from '@/hooks/useApi/useGLCodes';
import useTransactionCodes from '@/hooks/useApi/useTransactionCodes';
import { UtilButton } from 'UI';
import useAccountBalance from '@/hooks/useApi/useAccountBalance';
import { useTranslation, Trans } from 'react-i18next';
import { useDataFetchIndicator } from '@/hooks';
import { BodyText, FieldData, InteracWordmark } from 'vopay';
import useContacts from '@/hooks/useApi/useContacts';
import useClientAccounts from '@/hooks/useApi/useClientAccounts';
import usePortalDropdownContacts from '@/hooks/useApi/usePortalDropdownContacts';
import MessageBox from '@/components/UI/MessageBox/MessageBox';
import GppGoodIcon from './svgs/gpp_good.svg';
import useFetchRailzBusinessAccount from '@/hooks/useApi/useFetchRailzBusinessAccount';
import { useDataDog } from '@/providers/Datadog.provider';

type SinglePaymentProps = {
  onSubmit: (values: PaymentFields) => void;
  defaultValues: PaymentFieldDefault;
  onAvailableBalanceChange: (balance: string) => void;
  onCurrencyChange?: (currency: SupportedCurrencies) => void;
};

const SinglePaymentForm: FunctionComponent<SinglePaymentProps> = ({
  defaultValues,
  onSubmit,
  onAvailableBalanceChange,
  onCurrencyChange,
}) => {
  const history = useHistory();
  const { t } = useTranslation('translations', { keyPrefix: 'SinglePayment.Form' });
  const { fetchContact } = useContacts();
  const { fetchClientAccount } = useClientAccounts();
  const [autoDepositEnabled, setAutoDepositEnabled] = useState(false);
  const [paymentMethods, setPaymentMethods] = useState<PaymentMethod[]>([]);
  const { logMessage } = useDataDog();
  const [moreInfoNeeded, setMoreInfoNeeded] = useState(false);
  const [previewVisible, setPreviewVisible] = useState(false);
  const { GLCodes, fetchGLCodes, loading: loadingGLCodes } = useGLCodes();
  const [, dataFetchIndicator] = useDataFetchIndicator();
  const { transactionCodes, fetchTransactionCodes, loading: loadingTransactionCodes } = useTransactionCodes();
  const { dropdownContacts, getDropdownItems, fetchingContacts } = usePortalDropdownContacts({
    includeBalance: true,
    includePaymentRails: true,
  });
  const {
    response: { AccountType, clientAccountsEnabled, Country, AccountingSoftwareIntegrationEnabled, LoginUserName },
  } = useClientData();
  const [isUnderReview, setIsUnderReview] = useState(false);
  const [isVisible, setIsVisible] = useState(false);
  const alertDialog = useAlertDialog();
  const { balance, fetchBalance } = useAccountBalance();
  const { fetchAutoDepositStatus, loading: loadingAutoDeposit } = useInteracAutoDeposit();
  const [, , , fetchRailzBusinessAccount] = useFetchRailzBusinessAccount({});
  const [accountingSoftwareHasAccounts, setAccountingSoftwareHasAccounts] = useState(false);

  const [clientAccountBalanceResponse, getClientAccountBalance] = useFetchClientAccountBalance({});
  const availableBalance = useMemo(() => {
    return clientAccountsEnabled ? clientAccountBalanceResponse.AvailableFunds : String(balance?.AvailableFunds || '');
  }, [clientAccountsEnabled, clientAccountBalanceResponse.AvailableFunds, String(balance?.AvailableFunds || '')]);

  useEffect(() => {
    dataFetchIndicator(loadingGLCodes);
  }, [loadingGLCodes]);
  useEffect(() => {
    dataFetchIndicator(loadingTransactionCodes);
  }, [loadingTransactionCodes]);
  useEffect(() => {
    dataFetchIndicator(loadingAutoDeposit);
  }, [loadingAutoDeposit]);

  useEffect(() => {
    const checkRailzAccounts = async () => {
      if (AccountingSoftwareIntegrationEnabled && LoginUserName) {
        try {
          const railzBusinessAccount = await fetchRailzBusinessAccount({
            PortalUser: LoginUserName,
          });

          if (!railzBusinessAccount.Success) {
            throw new Error(railzBusinessAccount.ErrorMessage);
          }

          const accountHasBeenSetUp = Object.values(railzBusinessAccount.Accounts).length > 0;
          setAccountingSoftwareHasAccounts(accountHasBeenSetUp);
        } catch (error) {
          // no error message display because it returns an error if railz isn't setup which in this case is the same as not having an accounting software connected
          console.error(error);
          setAccountingSoftwareHasAccounts(false);
        }
      } else {
        setAccountingSoftwareHasAccounts(false);
      }
    };

    checkRailzAccounts();
  }, []);

  useEffect(() => {
    onAvailableBalanceChange(availableBalance || '');
  }, [availableBalance]);
  const [, , , fetchElinxSettings] = useFetchElinxSettings({});
  const lockedFeatures = useLockedFeatures();

  const { updateIsLocked } = useLockedFeatureDialogue();

  const hasBankAccount = useMemo(
    () => paymentMethods.some((pm) => pm.PaymentMethod === 'BankAccount'),
    [paymentMethods],
  );
  const [shouldShowAddressCard, setShouldShowAddressCard] = useState(false);
  const [isAddressRequired, setIsAddressRequired] = useState(false);
  const contactDetailsFields = useContactDetailsFields();
  const contactAddressFields = useContactAddressFields(Country, shouldShowAddressCard, isAddressRequired);
  const elinxDetailsFields = useElinxDetailsFields();
  const elinxBankAccountFields = useElinxBankAccountFields('Pay', Country);
  const transactionDetailsFields = useTransactionDetailsFields('Pay');
  const amountFields = useAmountFields(availableBalance || '', AccountType, 'Pay');
  const contactFields = useContactFields(dropdownContacts, getDropdownItems, fetchingContacts);
  const additionalInformationFields = useAdditionalInformation(
    GLCodes,
    transactionCodes,
    accountingSoftwareHasAccounts,
  );
  const paymentSettingsFields = usePaymentSettingsFields('Pay', Country, paymentMethods);
  const interacEtransferFields = useInteracEtransferFields('Pay', autoDepositEnabled);
  const bankAccountFields = useBankAccountFields(Country, clientAccountsEnabled);
  const elinxSettingFields = useElinxSettingsFields('email', Country);
  const elinxCardFields = useElinxCardFields();

  const fieldDataList: FieldData<PaymentFieldDefault>[] = useMemo(() => {
    return [
      ...contactFields,
      ...contactDetailsFields,
      ...amountFields,
      ...paymentSettingsFields,
      ...bankAccountFields,
      ...interacEtransferFields,
      ...elinxBankAccountFields,
      ...elinxCardFields,
      ...transactionDetailsFields,
      ...additionalInformationFields,
      ...contactAddressFields,
      ...elinxDetailsFields,
      ...elinxSettingFields,
    ];
  }, [
    transactionDetailsFields,
    additionalInformationFields,
    contactDetailsFields,
    contactAddressFields,
    amountFields,
    elinxBankAccountFields,
    elinxCardFields,
    elinxDetailsFields,
    paymentSettingsFields,
    bankAccountFields,
    interacEtransferFields,
    contactFields,
    elinxSettingFields,
  ]);

  const toggleMoreInformation = () => {
    setMoreInfoNeeded(!moreInfoNeeded);
  };
  const { permissions } = usePermissions();


  useEffect(() => {
    fetchGLCodes();
    fetchTransactionCodes();
    getDropdownItems('');
  }, []);

  const { renderFormElements, ctx } = useForm<PaymentFieldDefault, PaymentFields>({
    config: fieldDataList,
    initialValues: defaultValues,
    onSubmit: (serializedData, formCtx) => {
      onSubmit({
        ...serializedData,
        PaymentSelectionMethod: formCtx.getSerializedValue('PaymentSelectionMethod') || '',
        InvoiceFileName: formCtx.getSerializedValue('InvoiceFileName') || '',
        RecipientName: formCtx.getSerializedValue('RecipientName') || '',
        ClientReferenceNumber: formCtx.getSerializedValue('ClientReferenceNumber'),
        Address1: formCtx.getSerializedValue('Address1'),
        City: formCtx.getSerializedValue('City'),
        Province: formCtx.getSerializedValue('Province'),
        Country: formCtx.getSerializedValue('Country') || Country,
        PostalCode: formCtx.getSerializedValue('PostalCode'),
        RecipientPhoneNumber: formCtx.getSerializedValue('RecipientPhoneNumber'),
        RecipientEmailAddress: formCtx.getSerializedValue('RecipientEmailAddress'),
        SendNotification: formCtx.getSerializedValue('SendNotification'),
        SendReminder: formCtx.getSerializedValue('SendReminder'),
        NotificationMethod: formCtx.getSerializedValue('NotificationMethod'),
        ReminderFrequency: formCtx.getSerializedValue('ReminderFrequency'),
        ProvideOrRequestPaymentInformation: formCtx.getSerializedValue('ProvideOrRequestPaymentInformation'),
        AccountNumber: formCtx.getSerializedValue('AccountNumber'),
        ABARoutingNumber: formCtx.getSerializedValue('ABARoutingNumber'),
        FinancialInstitutionNumber: formCtx.getSerializedValue('FinancialInstitutionNumber'),
        BranchTransitNumber: formCtx.getSerializedValue('BranchTransitNumber'),
        FirstName: formCtx.getSerializedValue('FirstName'),
        LastName: formCtx.getSerializedValue('LastName'),
        CompanyName: formCtx.getSerializedValue('CompanyName'),
      });
    },
    isReviewing: isUnderReview,
  });
  const isNewContact = useMemo(() => ctx.values.Contact === 'new', [ctx.values.Contact]);
  const isNewPaymentMethod = useMemo(
    () => ctx.values.PaymentMethod === 'new' || ctx.values.PaymentMethod === 'request',
    [ctx.values.PaymentMethod],
  );
  const currency = useMemo(() => {
    return ctx.values.Currency !== '' ? ctx.values.Currency || 'CAD' : 'CAD';
  }, [ctx.values.Currency]);

  useEffect(() => {
    if (currency && onCurrencyChange) {
      //TODO - need to switch type to SupportedCurrencies throughout the form.
      const currencyMap = new Map<string, SupportedCurrencies>([
        ['CAD', 'CAD'],
        ['USD', 'USD'],
      ]);

      const mappedCurrency = currencyMap.get(currency) ?? 'CAD';
      onCurrencyChange(mappedCurrency);
    }
  }, [currency]);
  useEffect(() => {
    const sendRequest = () => {
      if (ctx.values.Contact && clientAccountsEnabled) {
        getClientAccountBalance({
          ClientAccountID: ctx.values.Contact,
          Currency: currency,
        });
      }
    };
    const timeoutRef = setTimeout(sendRequest, 100);
    return () => {
      clearTimeout(timeoutRef);
    };
  }, [ctx.values.Contact, currency]);

  useEffect(() => {
    if (!clientAccountsEnabled) {
      fetchBalance(ctx.values.Currency || 'CAD');
    }
  }, [ctx.values.Currency]);

  useEffect(() => {
    if (ctx.values.Contact && ctx.values.Contact !== 'new') {
      const contact = dropdownContacts.find((c) => c.id === ctx.values.Contact);
      setPaymentMethods(contact?.paymentRails || []);
    } else {
      setPaymentMethods([]);
    }
  }, [ctx.values.Contact]);

  useEffect(() => {
    if (!hasBankAccount && ctx.values.Contact && ctx.values.Contact !== 'new') {
      ctx.setFieldTouched('Contact', true);
    }
  }, [defaultValues, hasBankAccount]);

  useEffect(() => {
    if (clientAccountsEnabled) {
      if (ctx.values.PaymentMethod === 'Bank Account' && hasBankAccount) {
        ctx.setValues({ ...ctx.values, ProvideOrRequestPaymentInformation: 'provide' });
      }
    }
  }, [ctx.values.ClientAccount, ctx.values.ProvideOrRequestPaymentInformation, hasBankAccount]);

  const getElinxSettings = async () => {
    try {
      const res = await fetchElinxSettings({});
      if (!res.Success) {
        throw new Error(res.ErrorMessage);
      }
      setIsVisible(false);
      ctx.setValues({
        ...ctx.values,
        SendNotification: toBool(res.ELinxSettings.ReceiverNotificationsEnabled),
        SendReminder: toBool(res.ELinxSettings.ELinxReminderEnabled),
        ReminderFrequency: res.ELinxSettings.ELinxReminderFrequency,
        NotificationMethod: res.ELinxSettings.DefaultNotificationMethod,
      });
    } catch (err) {
      console.error(err);
      alertDialog.caution('Error', t('failedFetchElinx'), {
        abortLabel: t('dismiss'),
      });
    }
  };

  useEffect(() => {
    const controller = new AbortController();
    const signal = controller.signal;
    const debounceTimeout = setTimeout(async () => {
      if (
        ctx.values.Contact !== '' &&
        ctx.values.ProvideOrRequestPaymentInformation !== 'request' &&
        ctx.values.PaymentSelectionMethod === 'email' &&
        ctx.values.EtransferConnectionType === 'email' &&
        !ctx.errors.RecipientEmailAddress
      ) {
        try {
          const resp = await fetchAutoDepositStatus(ctx.values.RecipientEmailAddress);
          if (!signal.aborted) {
            setAutoDepositEnabled(resp.AutoDepositEnabled);
          }
        } catch {
          setAutoDepositEnabled(false);
        }
      }
    }, 500);
    if (ctx.values.EtransferConnectionType === 'phone') {
      setAutoDepositEnabled(false);
    }
    return () => {
      clearTimeout(debounceTimeout);
      controller.abort();
    };
  }, [
    ctx.values.RecipientEmailAddress,
    ctx.values.Contact,
    ctx.values.PaymentMethod,
    ctx.values.EtransferConnectionType,
    ctx.values.ProvideOrRequestPaymentInformation,
    ctx.values.PaymentSelectionMethod,
    ctx.errors.RecipientEmailAddress,
  ]);

  const transactionDetailsElements = useMemo(() => {
    return renderFormElements({ config: transactionDetailsFields });
  }, [renderFormElements, transactionDetailsFields]);

  const additionalInformationElements = useMemo(() => {
    return renderFormElements({ config: additionalInformationFields });
  }, [renderFormElements, additionalInformationFields]);

  const contactDetailsElements = useMemo(() => {
    return renderFormElements({ config: contactDetailsFields });
  }, [renderFormElements, contactDetailsFields]);

  const contactAddressElements = useMemo(() => {
    return renderFormElements({ config: contactAddressFields });
  }, [renderFormElements, contactAddressFields]);

  const paymentSettingsElements = useMemo(() => {
    return renderFormElements({ config: paymentSettingsFields });
  }, [renderFormElements, paymentSettingsFields]);

  const bankAccountElements = useMemo(() => {
    return renderFormElements({ config: bankAccountFields });
  }, [renderFormElements, bankAccountFields]);

  const interacEtransferElements = useMemo(() => {
    return renderFormElements({ config: interacEtransferFields });
  }, [renderFormElements, interacEtransferFields]);

  const elinxBankAccountElements = useMemo(() => {
    return renderFormElements({ config: elinxBankAccountFields });
  }, [renderFormElements, elinxBankAccountFields]);

  const elinxDetailsElements = useMemo(() => {
    return renderFormElements({ config: elinxDetailsFields });
  }, [renderFormElements, elinxDetailsFields]);

  const elinxCardElements = useMemo(() => {
    return renderFormElements({ config: elinxCardFields });
  }, [renderFormElements, elinxCardFields]);

  const elinxSettingElements = useMemo(() => {
    return renderFormElements({
      config: elinxSettingFields,
    });
  }, [renderFormElements, elinxSettingFields]);

  const amountElements = useMemo(() => {
    return renderFormElements({ config: amountFields });
  }, [renderFormElements, amountFields]);

  const contactElements = useMemo(() => {
    return renderFormElements({ config: contactFields });
  }, [renderFormElements, contactFields]);

  useEffect(() => {
    setIsAddressRequired(
      !!(
        ctx.values.PaymentSelectionMethod?.includes('bank') &&
        ctx.values.ProvideOrRequestPaymentInformation === 'provide'
      ),
    );
  }, [ctx.values.PaymentSelectionMethod, ctx.values.ProvideOrRequestPaymentInformation]);

  useEffect(() => {
    const checkAddressFields = async () => {
      if (ctx.values.Contact === 'new') {
        setShouldShowAddressCard(true);
        return;
      }

      if (!isAddressRequired) {
        setShouldShowAddressCard(false);
        return;
      }

      if (!ctx.values.Contact) {
        setShouldShowAddressCard(false);
        return;
      }

      try {
        let address1 = '';
        let city = '';
        let province = '';
        let country = '';
        let postalCode = '';
        let contactPhone = '';

        if (clientAccountsEnabled) {
          const account = await fetchClientAccount(ctx.values.Contact);

          if (account) {
            address1 = account.Address1 || '';
            city = account.City || '';
            province = account.Province || '';
            country = account.Country || '';
            postalCode = account.PostalCode || '';
            contactPhone = account.PhoneNumber || '';
          }
        } else {
          const contact = await fetchContact(ctx.values.Contact);
          if (contact) {
            address1 = contact.Address1 || '';
            city = contact.City || '';
            province = contact.Province || '';
            country = contact.Country || '';
            postalCode = contact.PostalCode || '';
            contactPhone = contact.PhoneNumber || '';
          }
        }

        const bankPhone = ctx.values.BankPhoneNumber || '';

        const hasAddress1 = address1.trim() !== '';
        const hasCity = city.trim() !== '';
        const hasProvince = province.trim() !== '';
        const hasCountry = country.trim() !== '';
        const hasPostalCode = postalCode.trim() !== '';

        // Helper function to check if phone is valid (not empty and not default '0000000000')
        const isValidPhone = (phone: string) => phone.trim() !== '' && phone !== '0000000000';

        // Check if phone number is needed (for US)
        let phoneNeeded = Country === 'US' && isAddressRequired;
        let hasValidPhone = false;

        if (phoneNeeded) {
          // Determine which phone to use: contact phone first, then bank phone
          let phoneToUse = '';
          if (isValidPhone(contactPhone)) {
            phoneToUse = contactPhone;
          } else if (isValidPhone(bankPhone)) {
            phoneToUse = bankPhone;
          }

          hasValidPhone = phoneToUse !== '';

          // Set RecipientPhoneNumber if we have a phone
          if (hasValidPhone && !ctx.values.RecipientPhoneNumber) {
            ctx.setFieldValue('RecipientPhoneNumber', phoneToUse);
          }
        }

        const addressComplete = hasAddress1 && hasCity && hasProvince && hasCountry && hasPostalCode;
        const allFieldsComplete = phoneNeeded ? addressComplete && hasValidPhone : addressComplete;

        setShouldShowAddressCard(!allFieldsComplete);
      } catch (err) {
        console.error('Error fetching address:', err);
        setShouldShowAddressCard(true);
      }
    };

    checkAddressFields();
  }, [ctx.values.Contact, isAddressRequired, clientAccountsEnabled, Country, ctx.values.BankPhoneNumber]);
  return (
    <div>
      <form
        onSubmit={(e) => {
          if (!isUnderReview) {
            setIsUnderReview(true);
            window.scrollTo({ top: 0, behavior: 'smooth' });
          } else {
            ctx.handleSubmit(e);
          }
          e.preventDefault();
        }}
        className={classes('form')}
      >
        <div className={classes('formContainer')}>
          <Box grow="1" flx="col" className={classes('mainContent')}>
            <Box flx="col" gapSize="24px">
              <Card className={classes('card')}>
                <Box flx="col" gapSize="32px" height="auto">
                  <div className={classes('grid')}>
                    {contactElements}
                    {isNewContact && contactDetailsElements}
                  </div>

                  <div className={classes('grid')}>
                    <div className={classes('currencyFeild')}>{amountElements}</div>
                  </div>

                  <div className={classes('grid')}>{paymentSettingsElements}</div>
                  {(isNewContact || isNewPaymentMethod || ctx.values.PaymentSelectionMethod === 'email') && (
                    <>
                      {ctx.values.PaymentSelectionMethod?.includes('bank') &&
                        ctx.values.ProvideOrRequestPaymentInformation === 'request' && (
                          <Card className={classes('subCard')} hoverEffect={false} title={t('bankAccountTitle')}>
                            <div className={classes('grid')}>{elinxBankAccountElements}</div>
                          </Card>
                        )}
                      {ctx.values.PaymentSelectionMethod?.includes('bank') &&
                        ctx.values.ProvideOrRequestPaymentInformation === 'provide' && (
                          <Card className={classes('subCard')} hoverEffect={false} title={t('bankAccountTitle')}>
                            <div className={classes('grid')}>{bankAccountElements}</div>
                          </Card>
                        )}
                      {ctx.values.PaymentSelectionMethod?.includes('email') &&
                        ctx.values.ProvideOrRequestPaymentInformation === 'provide' && (
                          <Card
                            className={classes('subCard')}
                            hoverEffect={false}
                            title={
                              <Trans i18nKey="interacTitle" t={t}>
                                <InteracWordmark />
                              </Trans>
                            }
                          >
                            <Box flx="col" gap="md" base="8">
                              <div className={classes('grid')}>{interacEtransferElements}</div>
                              {autoDepositEnabled && (
                                <MessageBox>
                                  <Box flx="row" ai="center" gapSize="16px">
                                    <img src={GppGoodIcon} alt="good icon" />
                                    <div>
                                      This contact has <span style={{ fontWeight: 600 }}>auto deposit</span> enabled, so
                                      no security question is needed. Please note that this transfer cannot be
                                      cancelled.
                                    </div>
                                  </Box>
                                </MessageBox>
                              )}
                            </Box>
                          </Card>
                        )}
                      {(ctx.values.PaymentSelectionMethod?.includes('credit') ||
                        ctx.values.PaymentSelectionMethod?.includes('debitcard')) &&
                        ctx.values.ProvideOrRequestPaymentInformation === 'request' && (
                          <Card
                            className={classes('subCard')}
                            hoverEffect={false}
                            title={t('cardSettingsTitle', { defaultValue: 'Card Settings' })}
                          >
                            <div className={classes('grid')}>{elinxCardElements}</div>
                          </Card>
                        )}
                    </>
                  )}
                  <div className={classes('grid')}>{transactionDetailsElements}</div>
                </Box>
                {!moreInfoNeeded && !isUnderReview && (
                  <UtilButton
                    size="large"
                    iconLeft="add"
                    onClick={() => toggleMoreInformation()}
                    disabled={isUnderReview}
                    className={classes('showMoreButton')}
                  >
                    {t('addMoreInformationBtn')}
                  </UtilButton>
                )}
                {(moreInfoNeeded || isUnderReview) && (
                  <>
                    <UtilButton
                      size="large"
                      iconLeft="remove"
                      onClick={() => toggleMoreInformation()}
                      disabled={isUnderReview}
                      className={classes('showMoreButton')}
                    >
                      {t('hideAdditionalInformationBtn')}
                    </UtilButton>

                    <div className={classes('grid', 'additionalInformationContainer')}>
                      {additionalInformationElements}
                    </div>
                  </>
                )}
              </Card>

              {shouldShowAddressCard && !isAddressRequired && (
                <CollapsibleCard className={classes('collapsibleCard')}>
                  <CollapsibleCard.Trigger indicator="chevron" style={{ '--collapsibleCardPadding': '24px' }}>
                    <Box gap="sm" ai="center">
                      <span>{Country === 'US' ? t('phoneAndAddressTitle') : t('addressTitle')}</span>
                    </Box>
                  </CollapsibleCard.Trigger>

                  <CollapsibleCard.Content style={{ '--collapsibleCardPadding': '24px' }}>
                    <div className={classes('grid')}>{contactAddressElements}</div>
                  </CollapsibleCard.Content>
                </CollapsibleCard>
              )}
              {shouldShowAddressCard && isAddressRequired && (
                <Card title={Country === 'US' ? t('phoneAndAddressTitle') : t('addressTitle')}>
                  <div className={classes('grid')}>{contactAddressElements}</div>
                </Card>
              )}
              {ctx.values.ProvideOrRequestPaymentInformation === 'request' && (
                <Card title={t('eLinxDetailsTitle')}>
                  <div className={classes('gridElinxDetails')}>{elinxDetailsElements}</div>
                </Card>
              )}
              {ctx.values.ProvideOrRequestPaymentInformation === 'request' && (
                <Card title={t('notificationSettingsTitle')}>
                  <div className={classes('grid')}>{elinxSettingElements}</div>
                </Card>
              )}
            </Box>
            <Box jc="sb" ai="fe" pt="md">
              <Box jc="fs">
                {ctx.values.ProvideOrRequestPaymentInformation === 'request' && (
                  <Button
                    colorScheme="secondary"
                    type="submit"
                    onClick={() => {
                      !lockedFeatures.PayLinkEnabled || !permissions.ViewElinxSettings
                        ? updateIsLocked(true, 'Elinx')
                        : setIsVisible(true);
                    }}
                  >
                    {t('editElinxSettingsBtn')}
                  </Button>
                )}
              </Box>
              <Box flx="row" gap="common" ai="fe">
                {ctx.values.ProvideOrRequestPaymentInformation === 'request' && (
                  <Box jc="fe">
                    <Button
                      id="See-Preview"
                      colorScheme="secondary"
                      type="button"
                      onClick={() => setPreviewVisible(true)}
                    >
                      {t('previewElinxBtn')}
                    </Button>
                  </Box>
                )}

                <Box jc="fe" gap="sm">
                  {isUnderReview ? (
                    <Button id="Fund-back" colorScheme="secondary" onClick={() => setIsUnderReview(false)}>
                      {t('backBtn')}
                    </Button>
                  ) : null}
                  <div
                    onClick={() => {
                      if (!ctx.isValid) {
                        logMessage('Disabled submit clicked', 'warn', {
                          feature: 'Single Payment',
                          errors: ctx.errors,
                          values: ctx.values,
                        });
                      }
                    }}
                  >
                    <Button id="Fund-submit" disabled={!ctx.isValid} colorScheme="primary" type="submit">
                      {isUnderReview ? t('submitBtn') : t('reviewBtn')}
                    </Button>
                  </div>
                </Box>
              </Box>
            </Box>
          </Box>
        </div>
        <Prompt when={ctx.hasPendingData} message={t('pendingDataMessage')} />

        <Modal isVisible={previewVisible} enableBgClick={false}>
          <div className={classes('container')}>
            <Box flx="col" ai="center">
              <div className={classes('titleContainer')}>
                <Title>{t('ElinxPreview.title')}</Title>
              </div>
              <div className={classes('descriptionContainer')}>
                <BodyText>{t('ElinxPreview.description1')}</BodyText>
                <BodyText>{t('ElinxPreview.description2')}.</BodyText>
              </div>
              <div>
                <img src={ElinxImage} alt="elinx-preview" />
              </div>
              <div className={classes('buttonBar')}>
                <Button
                  id="ManageBranding-Settings"
                  colorScheme="secondary"
                  type="button"
                  onClick={() => {
                    history.push('/settings/brand-settings');
                  }}
                >
                  {t('ElinxPreview.manageBtn')}
                </Button>
                <Button
                  id="AddLabel-Accept"
                  colorScheme="primary"
                  type="button"
                  onClick={() => {
                    setPreviewVisible(false);
                  }}
                >
                  {t('ElinxPreview.closeBtn')}
                </Button>
              </div>
            </Box>
          </div>
        </Modal>
      </form>
      <ElinxSettingsDialogue
        isVisible={isVisible}
        onCloseModal={() => setIsVisible(false)}
        onFetchElinxSettings={getElinxSettings}
      />
    </div>
  );
};

export default SinglePaymentForm;
